import cs from 'classnames'
import { useTranslation } from 'next-i18next'
import { path, sortBy } from 'ramda'
import React, { useEffect, useState } from 'react'

import { CertificateLinkForSeries } from '../learning-module/certificate/DownloadCertificateLink'
import ModuleRow from '../../../components/Learning/learning-modules/rows/ModuleRow'
import { LearningSeriesType } from '../../../graphql/learning'
import { LearningCardRating } from '../../../routes/components/Rating'
import {
  ListGridButtonGroup,
  useViewTypeFromSessionStorage,
} from '../../../routes/groups/components/entity/learning-series-list'
import { HTMLDescription } from '../../HTMLDescription'
import Breadcrumbs from './Breadcrumbs'
import { CurrentUserModuleStatus, ViewType } from './rows/types'

import { useRouter } from 'next/router'
import { useSelector } from '../../../react-redux'
import { selectAuthenticatedUser } from '../../../modules/auth/selectors'
import { wrapWithLayout, wrapWithTable } from './SeriesList'

type Props = {
  isLoading: boolean
  heading?: React.ReactElement
  showGotoDashboardButton?: boolean
  forceCardViewAndHideActions?: boolean
  showBreadcrumbs?: boolean
  returnToSeries?: boolean
  showDetails?: boolean
  alreadySorted?: boolean
  results: any
  series?: LearningSeriesType
}

const ModulesList = ({
  showGotoDashboardButton = false,
  isLoading,
  heading,
  forceCardViewAndHideActions = false,
  showBreadcrumbs = false,
  returnToSeries,
  alreadySorted,
  showDetails = false,
  results,
  series,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const router = useRouter()
  const currentUser = useSelector(selectAuthenticatedUser)
  const [viewType, setViewType] = useViewTypeFromSessionStorage()
  const [searchInput, setSearchInput] = useState('')
  const [modulesList, setModulesList] = useState([])

  const sortSetting = alreadySorted ? undefined : series?.sort_modules_by

  // Module list filtering
  useEffect(() => {
    let modules = results?.filter(item => {
      return (
        item.revision?.title.toLowerCase().indexOf(searchInput.toLowerCase()) >
        -1
      )
    })

    if (!alreadySorted) {
      modules = sortBy(
        path(
          {
            alphabetical: ['revision', 'title'],
            published_date: ['revision', 'created_at'],
            manual: ['module', 'position'],
          }[sortSetting] || ['module', 'position']
        ),
        modules
      )
    }

    const mappedModules = modules?.map((item, ix) => {
      const revision = {
        module: {
          ...item,
          revision: {
            ...item.revision,
            series: item.series,
          },
        },
        revision: {
          ...item.revision,
          series: item.series,
        },
      }

      const session = revision.module.latestSession
        ? revision.module.latestSession[0]
        : undefined

      const currentUserModuleStatus = session?.active
        ? CurrentUserModuleStatus.inProgress
        : session?.events && session.events.find(x => x.event === 'finish')
          ? CurrentUserModuleStatus.completed
          : CurrentUserModuleStatus.null

      return (
        <ModuleRow
          key={revision.revision.module_id}
          session={session}
          moduleStatus={revision.module.status}
          module={revision.module}
          revision={revision.revision}
          series={item.series}
          currentUserModuleStatus={currentUserModuleStatus}
          viewType={forceCardViewAndHideActions ? ViewType.cards : viewType}
          ix={ix}
          showGotoDashboardButton={showGotoDashboardButton}
          returnToSeries={returnToSeries}
          modulesList={item.series.modules}
        />
      )
    })

    setModulesList(mappedModules)
  }, [results, viewType, searchInput])

  const rating = series?.star_rating?.rating

  return (
    <div style={{ padding: `${showBreadcrumbs ? '0px' : '20px'} 10px 20px` }}>
      {showBreadcrumbs && <Breadcrumbs />}

      {showDetails && (
        <div
          className="learning-series__header"
          style={{
            borderBottom: 'solid 1px rgba(0, 0, 0, 0.1)',
            marginBottom: '20px',
          }}
        >
          <h1 style={{ marginBottom: '10px' }}>{series?.title}</h1>
          {rating >= 3 && (
            <div style={{ marginBottom: '15px' }}>
              <LearningCardRating score={rating} />{' '}
            </div>
          )}
          <HTMLDescription
            description={
              series.rich_description?.length
                ? series.rich_description
                : series.description
            }
            className="learning-item__description"
          />
          {currentUser && series.accreditation_when === 'series' ? (
            <PrintSeriesCertificate series={series} currentUser={currentUser} />
          ) : null}
          {!currentUser && (
            <div style={{ marginBottom: '20px' }}>
              <a
                href={`/join?referer=${router.asPath}`}
                className={cs(
                  'btn btn-primary btn--fill btn--block btn--center-text'
                )}
                style={{ maxWidth: '250px' }}
              >
                {series?.payments_enabled
                  ? 'Sign up to subscribe'
                  : 'Sign up to start learning'}
              </a>
            </div>
          )}
        </div>
      )}

      {!forceCardViewAndHideActions && (
        <div className="learning-list__controls">
          <div>{!showDetails && heading}</div>
          <div className="learning-list__controls__input-and-btn-group-wrapper">
            <input
              placeholder="Search modules..."
              className="fs-unmask search-field__input learning-list__search-field"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
            <ListGridButtonGroup
              viewType={viewType}
              setViewType={setViewType}
            />
          </div>
        </div>
      )}

      {isLoading && (
        <div style={{ padding: '20px 0px' }}>
          {t('common:ui.activity_indicator')}
          ...
        </div>
      )}

      {!isLoading && modulesList?.length === 0
        ? t('learning:learning.library_no_modules_to_show')
        : viewType === ViewType.table
          ? wrapWithTable(modulesList)
          : wrapWithLayout(modulesList)}
    </div>
  )
}

const PrintSeriesCertificate = ({ series, currentUser }) => {
  return (
    <CertificateLinkForSeries
      series={series}
      certificateUrl={series.earned_cme?.certificate_url}
      button
    />
  )
}

export default ModulesList
